import { XIcon } from "lucide-react";
import { useTranslate } from "ra-core";
import type { ComponentProps, ReactNode } from "react";

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useTapThroughGuard } from "@/lib/tapThroughGuard";
import { cn } from "@/lib/utils";

import { useMobileFormGuard } from "./useMobileFormGuard";

/**
 * Shell shared by the create/edit sheets. Desktop keeps the original
 * full-height bottom sheet; below `md` it becomes a full-screen form sized to
 * the visual viewport (keyboard-aware), with a safe-area glass header holding
 * a close button, an internally scrolling body and a footer that stays above
 * the keyboard and the home indicator. Back closes it (see useMobileFormGuard).
 *
 * The mobile/desktop split is done with `max-md:`/`md:` classes, so there is
 * no layout flash while `useIsMobile` resolves.
 */

export interface FormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  /** Routed forms already own a history entry: pass false. */
  history?: boolean;
  className?: string;
}

export const FormSheet = ({
  open,
  onOpenChange,
  children,
  history = true,
  className,
}: FormSheetProps) => {
  useMobileFormGuard(open, () => onOpenChange(false), { history });
  useTapThroughGuard(open);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className={cn(
          "mobile-form-sheet data-[side=bottom]:h-dvh flex flex-col max-md:gap-0",
          className,
        )}
        aria-describedby={undefined}
      >
        {children}
      </SheetContent>
    </Sheet>
  );
};

/** Class for the <Form> placed directly inside a FormSheet. */
export const FORM_SHEET_FORM_CLASS =
  "h-dvh flex-1 flex flex-col max-md:h-auto max-md:min-h-0";

export interface FormSheetHeaderProps {
  title: ReactNode;
  actions?: ReactNode;
}

export const FormSheetHeader = ({ title, actions }: FormSheetHeaderProps) => {
  const translate = useTranslate();
  return (
    <SheetHeader className="mobile-form-header border-b">
      <div
        className={cn(
          "flex items-center gap-2 max-md:min-h-[var(--mobile-control)]",
          actions && "md:pr-12",
        )}
      >
        <SheetClose
          data-mobile-form-close=""
          className="liquid-glass glass-press flex size-[var(--mobile-control)] shrink-0 items-center justify-center rounded-full outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 md:hidden"
        >
          <XIcon className="size-5" strokeWidth={2} />
          <span className="sr-only">
            {translate("ra.action.close", { _: "Close" })}
          </span>
        </SheetClose>
        <SheetTitle className="min-w-0 flex-1 truncate">
          {typeof title === "string" ? (
            <span className="text-xl font-semibold max-md:text-lg">
              {title}
            </span>
          ) : (
            title
          )}
        </SheetTitle>
        {actions && <div className="shrink-0">{actions}</div>}
      </div>
    </SheetHeader>
  );
};

export const FormSheetBody = ({
  className,
  ...props
}: ComponentProps<"div">) => (
  <div
    className={cn(
      "flex-1 overflow-y-auto flex flex-col gap-3 p-4 max-md:min-h-0 max-md:overscroll-contain",
      className,
    )}
    {...props}
  />
);

export const FormSheetFooter = ({
  className,
  ...props
}: ComponentProps<"div">) => (
  <SheetFooter
    className={cn(
      "mobile-form-footer border-t flex flex-row w-full gap-4",
      className,
    )}
    {...props}
  />
);
